import type { ChangeEvent } from 'react';
import React from 'react';
import { useField } from 'formik';
import type { TextFieldProps } from '@mui/material/TextField';
import MuiTextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'next-i18next';

import { Error } from 'src/components/ui/TextField';
import Icon from 'src/components/ui/Icon';
import type { Props as IconProps } from 'src/components/ui/Icon';

import { colours } from 'src/constants/colours';
import { styledComponent, styledTag } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

export type Item = {
    value: string | number;
    label: string;
};

const Container = styledTag('div')`
    width: fit-content;
`;
const StyledTextField = styledTag('div')`
    .MuiInputBase-root {
        border-radius: 0.25rem;
        border: 0.0625rem solid transparent;
    }
    .MuiSelect-select  {
        padding-left: 2rem;
        padding-right: 0.5rem!important;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        color: ${colours.primary.main};
        font-weight: bold;
        border-radius: 0.25rem;
    }
    .Mui-focused {
        border: 0.0625rem solid ${colours.primary.O60};
        background-color: ${colours.blackOpacity.O08};
    }
    .MuiSelect-select:hover {
        background-color: ${colours.blackOpacity.O08};
    }
    .MuiSelect-icon {
        left: 0.5rem;
        top: 0.6rem;
    }
`;

const StyledMenuItem = styledComponent(MenuItem)`
    font-size: 0.875rem;
`;

const SortIconComponent = (props: IconProps) => (
    <Icon {...props} name="OrderBy" size="20" color={colours.primary.main} />
);

const StyledTextFieldAsMuiTextField = StyledTextField.withComponent(MuiTextField);

interface SortValues {
    createdAt?: string;
    price?: string;
}

type Props = TextFieldProps & {
    label?: string;
    name: string;
    helperText?: string;
    onChange?: (event: ChangeEvent<{ value: unknown }>) => void;
};
const OrderBySelect: React.FC<Props> = ({ name, label, onChange, ...rest }) => {
    const { t } = useTranslation(['ad']);
    const { logEventDynamic } = useDynamicLogEvents();

    const [field, meta, helpers] = useField(name);
    const { setValue } = helpers;
    const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
        if (onChange) {
            onChange(event);
        }
        if (event.target.value) {
            const parsedValue: SortValues = JSON.parse(event.target.value as string) as SortValues;
            if ('createdAt' in parsedValue && parsedValue.createdAt === 'DESC') {
                logEventDynamic(AnalyticsEvent.SortRecent);
            } else if ('price' in parsedValue) {
                if (parsedValue.price === 'ASC') {
                    logEventDynamic(AnalyticsEvent.SortPriceAscending);
                } else if (parsedValue.price === 'DESC') {
                    logEventDynamic(AnalyticsEvent.SortPriceDescending);
                }
            }
        }
        setValue(event.target.value);
    };
    const items = [
        {
            label: t('ad:newest'),
            value: JSON.stringify({ createdAt: 'DESC' }),
        },
        {
            label: t('ad:priceAsc'),
            value: JSON.stringify({ price: 'ASC' }),
        },
        {
            label: t('ad:priceDesc'),
            value: JSON.stringify({ price: 'DESC' }),
        },
    ];

    return (
        <Container>
            <StyledTextFieldAsMuiTextField
                select
                {...rest}
                {...field}
                id={name}
                variant="standard"
                name={name}
                label={label}
                error={Boolean(meta.touched && meta.error)}
                onChange={handleChange}
                InputLabelProps={{
                    shrink: true,
                }}
                SelectProps={{
                    ...rest.SelectProps,
                    IconComponent: SortIconComponent,
                }}
                InputProps={{
                    ...rest.InputProps,
                    disableUnderline: true,
                }}
            >
                {items.map((item: Item, index: number) => (
                    <StyledMenuItem key={index} value={item.value}>
                        {item.label}
                    </StyledMenuItem>
                ))}
            </StyledTextFieldAsMuiTextField>
            {meta.touched && meta.error && <Error>{meta.error}</Error>}
        </Container>
    );
};

export default OrderBySelect;
